import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { authService } from '../services/authService';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const ResetPasswordForm: React.FC = () => {
  useAuth();
  const { select } = useLanguage();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validToken, setValidToken] = useState(false);

  useEffect(() => {
    // Check if we have a valid reset token
    const checkResetToken = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        // If we have a session, the user is authenticated (via OTP or Link) and can reset password
        if (session?.user) {
          setValidToken(true);
        } else {
          // Check URL for reset token (Legacy/Implicit flow)
          const urlParams = new URLSearchParams(window.location.search);
          const accessToken = urlParams.get('access_token');
          const refreshToken = urlParams.get('refresh_token');
          
          if (accessToken && refreshToken) {
            setValidToken(true);
          } else {
            setError(select({ es: 'Enlace de recuperación inválido o expirado', en: 'Recovery link invalid or expired' }));
          }
        }
      } catch {
        setError(select({ es: 'Error al verificar el enlace de recuperación', en: 'Error verifying recovery link' }));
      }
    };

    void checkResetToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validatePassword = (password: string) => {
    if (password.length < 6) {
      return select({ es: 'La contraseña debe tener al menos 6 caracteres', en: 'Password must be at least 6 characters' });
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return select({ es: 'La contraseña debe contener al menos una letra minúscula', en: 'Password must contain at least one lowercase letter' });
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return select({ es: 'La contraseña debe contener al menos una letra mayúscula', en: 'Password must contain at least one uppercase letter' });
    }
    if (!/(?=.*\d)/.test(password)) {
      return select({ es: 'La contraseña debe contener al menos un número', en: 'Password must contain at least one number' });
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate passwords
      const passwordError = validatePassword(newPassword);
      if (passwordError) {
        setError(passwordError);
        return;
      }

      if (newPassword !== confirmPassword) {
        setError(select({ es: 'Las contraseñas no coinciden', en: 'Passwords do not match' }));
        return;
      }

      // Update password
      await authService.updatePassword(newPassword);
      
      setSuccess(true);
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);

    } catch (err) {
      setError(err instanceof Error ? err.message : select({ es: 'Error al actualizar la contraseña', en: 'Error updating password' }));
    } finally {
      setLoading(false);
    }
  };

  if (!validToken && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>{select({ es: 'Verificando enlace de recuperación...', en: 'Verifying recovery link...' })}</p>
        </div>
      </div>
    );
  }

  if (!validToken && error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 p-4">
        <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="text-red-500" size={32} />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {select({ es: 'Enlace inválido o expirado', en: 'Invalid or expired link' })}
            </h2>
            <p className="text-gray-600 mb-6">
              {error}
            </p>
            <a 
              href="/"
              className="inline-block w-full bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 transition-colors duration-200"
            >
              {select({ es: 'Volver al inicio', en: 'Back to home' })}
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
        <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-white" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{select({ es: '¡Contraseña actualizada!', en: 'Password updated!' })}</h2>
            <p className="text-gray-600 mb-4">
              {select({ es: 'Tu contraseña ha sido cambiada exitosamente. Serás redirigido al inicio en unos segundos.', en: 'Your password has been changed successfully. You will be redirected to the home page in a few seconds.' })}
            </p>
            <div className="text-sm text-gray-500">
              {select({ es: 'Redirigiendo en 3 segundos...', en: 'Redirecting in 3 seconds...' })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 p-4">
      <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="https://soartistic.com/wp-content/uploads/2025/06/logo-blanco-sin-fondo.svg" 
            alt="SoArtistic" 
            className="h-16 mx-auto mb-4 brightness-0"
          />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{select({ es: 'Nueva contraseña', en: 'New password' })}</h2>
          <p className="text-gray-600">{select({ es: 'Crea una contraseña segura para tu cuenta', en: 'Create a secure password for your account' })}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {select({ es: 'Nueva contraseña', en: 'New password' })}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {select({ es: 'Confirmar contraseña', en: 'Confirm password' })}
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Password requirements */}
          <div className="bg-gray-50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-gray-700 mb-2">{select({ es: 'Requisitos de la contraseña:', en: 'Password requirements:' })}</h4>
            <div className="space-y-1 text-xs">
              <div className={`flex items-center gap-2 ${newPassword.length >= 6 ? 'text-green-600' : 'text-gray-500'}`}>
                <div className={`w-2 h-2 rounded-full ${newPassword.length >= 6 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                {select({ es: 'Al menos 6 caracteres', en: 'At least 6 characters' })}
              </div>
              <div className={`flex items-center gap-2 ${/(?=.*[a-z])/.test(newPassword) ? 'text-green-600' : 'text-gray-500'}`}>
                <div className={`w-2 h-2 rounded-full ${/(?=.*[a-z])/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                {select({ es: 'Una letra minúscula', en: 'One lowercase letter' })}
              </div>
              <div className={`flex items-center gap-2 ${/(?=.*[A-Z])/.test(newPassword) ? 'text-green-600' : 'text-gray-500'}`}>
                <div className={`w-2 h-2 rounded-full ${/(?=.*[A-Z])/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                {select({ es: 'Una letra mayúscula', en: 'One uppercase letter' })}
              </div>
              <div className={`flex items-center gap-2 ${/(?=.*\d)/.test(newPassword) ? 'text-green-600' : 'text-gray-500'}`}>
                <div className={`w-2 h-2 rounded-full ${/(?=.*\d)/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                {select({ es: 'Un número', en: 'One number' })}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !newPassword || !confirmPassword}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
          >
            {loading ? select({ es: 'Actualizando...', en: 'Updating...' }) : select({ es: 'Actualizar contraseña', en: 'Update password' })}
          </button>
        </form>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-purple-600 hover:text-purple-700 font-medium transition-colors duration-200"
          >
            {select({ es: 'Volver al inicio', en: 'Back to home' })}
          </a>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
